<template>
  <div class="tw-h-screen">
    <component
      :is="componentData[componentName].component"
      v-if="componentName"
      v-bind="componentData[componentName].props"
      v-on="componentData[componentName].events" />
    <CoreDialog
      key="dialog"
      :data="dialog" />
    <PasswordUpdateCallout
      v-if="resetPasswordUserId"
      :userId="resetPasswordUserId" />
    <portal
      :to="PORTAL_LEVELS.ONE"
      :order="1">
      <ExpirationAlertCallout />
    </portal>
    <ToastsWrapper />
  </div>
</template>

<script>
import _flattenDeep from 'lodash/flattenDeep'

import _cloneDeep from "lodash/cloneDeep"
import actionSettingsRoutes from 'frontend/routes/settingsRoutes/action'
import alertSettingsRoutes from 'frontend/routes/settingsRoutes/alert'
import alertsRoutes from 'frontend/routes/alerts'
import auth from 'frontcore/auth'
import CoreDialog from 'frontcore/components/Dialog'
import ExpirationAlertCallout from 'frontcore/domain/navigation-bar/licence-expiration/views/ExpirationAlertCallout'
import hseRisksRoutes from 'frontend/routes/hseRisks'
import improvementIdeasRoutes from 'frontend/routes/improvementIdeas'
import improvementIdeasSettingsRoutes from 'frontend/routes/settingsRoutes/improvementIdeas'
import lineBoard from 'frontend/routes/lineBoard'
import Login from 'frontcore/domain/authorization/view/Login'
import NotAuthorization from 'frontend/presentation/not-authorization/views/NotAuthorization'
import okFirstPartRoutes from 'frontend/routes/okFirstPart'
import PasswordUpdateCallout from 'frontcore/domain/authorization/view/PasswordUpdateCallout'
import peopleSettingsRoutes from 'frontend/routes/settingsRoutes/people'
import perimeterSettingsRoutes from 'frontend/routes/settingsRoutes/perimeter'
import removeLandingScreen from 'frontend/helpers/removeLandingScreen'
import rotationTableRoutes from 'frontend/routes/rotationTable'
import routineSettingsRoutes from 'frontend/routes/settingsRoutes/routine'
import ServerError from 'frontcore/components/ServerError'
import sfmSettingsRoutes from 'frontend/routes/settingsRoutes/sfm'
import shopFloorManagement from 'frontend/routes/shopFloorManagement'
import skillMatrixRoutes from 'frontend/routes/skillMatrix'
import swaSettingsRoutes from 'frontend/routes/settingsRoutes/swa'
import ToastsWrapper from "frontcore/presentation/alert/views/ToastsWrapper.vue"
import toolSettingsRoutes from 'frontend/routes/settingsRoutes/tool'
import useApplicationFirstFetch from 'frontend/data/landing/useApplicationFirstFetch'
import useAuditSourcesStore from 'frontend/store/sources/useAuditSourcesStore'
import useConfigurationBaseStore from 'frontcore/store/configurations/useConfigurationBaseStore'
import useNeilaConfigurationStore from 'frontcore/store/neila/useNeilaConfigurationStore'
import visualStandardRSRSCharacteristicsSettingsRoutes from 'frontend/routes/settingsRoutes/visualStandardRSRSCharacteristics'
import {
  actionRoutes,
  auditsActionsEvidencesRoute,
  auditSettingsComplianceEvaluationReasonsRoute,
  auditSettingsComplianceThresholdsRoute,
  auditSettingsImportSkillsRoute,
  auditSettingsNoCompliancesRoute,
  auditSettingsObserversRoute,
  auditSettingsPlannerConfiguration,
  auditSettingsPreviousCommentsTimeRoute,
  auditSettingsQualityFailuresRoute,
  auditSettingsSubtypesConfiguration,
  auditSettingsSubtypesDetails,
  auditSettingsTemplateAssigmentRoute,
  auditSettingsTemplateRoute,
  chartsConfigRoute,
  commonRoutes,
  completedTasksRoutes,
  consolidatedReportInCorporativeRoutes,
  createNewRoutesAudits,
  dailyRoundCheckpointsRoutes,
  dailyroundRoutes,
  diagnosisRoutes,
  gembaWalkCheckpointsRoutes,
  loogBookRoutes,
  lpaRoutes,
  myCalendar,
  pendingRoutes,
  problemSolvingRoutes,
  settingsRoutes,
  skillMatrixSettingsPeopleIdentifierFormat,
  standardViewerRoutes,
  standardWorkReviewRoutes,
  virtualTrainingViewerRoutes,
} from 'frontend/routes/router'
import { APP_TYPE, CONFIG_TYPE } from 'frontend/constants/roles'
import { CONFIGURATION_KEYS, PORTAL_LEVELS } from 'frontcore/constants/constants'
import { createNewRoutesProductionHierarchy } from 'frontend/routes/productionHierarchy'
import { DASHBOARD_MODULE_TOKENS } from 'frontend/constants/settings'
import { defineAsyncComponent } from 'vue'
import { getYear } from 'frontend/helpers/dateUtils'
import { HTTP_ERROR_BAD_REQUEST } from 'frontcore/constants/'
import { ILUO_SOURCE } from 'frontend/constants/audit'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { masterAuditSettingsTemplateRoute } from 'frontend/routes/settingsRoutes/masterAuditTemplates'
import { ProfilePasswordExpiredError } from 'frontcore/errors/profile'
import { settingsShiftRoutesOptions, settingsSystemRoutesOptions} from 'frontend/routes/settingsRoutes/system'
import { setupI18n } from 'frontcore/i18n'
import { useDeviceConfigurationStore } from 'frontcore/store/configurations/useDeviceConfigurationStore'
import { useProfileStore } from 'frontcore/store/useProfileStore'

const App = defineAsyncComponent(() => import('./App.vue'))
const ERROR_PERMISSION_ROLE = 'Role without permission'

export default {
  components: {
    App,
    CoreDialog,
    ExpirationAlertCallout,
    Login,
    NotAuthorization,
    PasswordUpdateCallout,
    ServerError,
    ToastsWrapper,
  },
  setup () {
    const baseConfiguration = useConfigurationBaseStore()
    const profileStore = useProfileStore()
    const { loadDeviceConfiguration } = useDeviceConfigurationStore()
    const routeToModuleMappings = {
      'sfm-root': DASHBOARD_MODULE_TOKENS.sfm.name,
      'line-board-root': DASHBOARD_MODULE_TOKENS.lineBoard.name,
    }

    return {
      ...useApplicationFirstFetch(),
      loadDeviceConfiguration,
      baseConfiguration,
      profileStore,
      routeToModuleMappings,
    }
  },
  data () {
    return {
      PORTAL_LEVELS,
      componentName: null,
      errorMsg: '',
      errorTitle: '',
      hasPrivacyPolicy: false,
      isFetchingUserConfiguration: false,
      resetPasswordUserId: null,
      showPrivacyPolicyModal: false,
    }
  },
  computed: {
    ...mapGetters([
      'getAllowEditShifts',
      'getConfigurationKeyByName',
      'getModulePermissions',
      'getProductionHierarchyLevels',
      'getRoleModules',
      'getRoleModulesByType',
      'includeSkillMatrix',
    ]),
    componentData () {
      return {
        App: {
          component: App,
          props: {},
          events: {},
        },
        Login: {
          component: Login,
          props: {
            applicationName: 'mPS',
            isFetchingUserConfiguration: this.isFetchingUserConfiguration,
          },
          events: {
            loginSuccess: this.onLoginSuccess,
          },
        },
        ServerError: {
          component: ServerError,
          props: {
            errorMsg: this.errorMsg,
            errorTitle: this.errorTitle,
          },
          events: {},
        },
        NotAuthorization: {
          component: NotAuthorization,
          props: {},
          events: {},
        },
      }
    },
    dialog () {
      return {
        template: 'info',
        open: this.showPrivacyPolicyModal,
        title: this.$t('common.messages.privacy_policy_title'),
        body: this.$t('common.messages.privacy_policy'),
        firstButton: { text: this.$t('common.messages.confirm'), template: 'default' },
        secondButton: '',
        onConfirmCallback: this.hideModal,
      }
    },
  },
  watch: {
    componentName (newValue) {
      if (newValue === null) { return }
      removeLandingScreen()
    },
  },
  created () {
    Promise.all([setupI18n(), this.baseConfiguration.fetchConfigurationBase()])
      .then(() => {
        this.setConfigurationBase()
        const pendingFullPath = this.$route.path !== '/' ? this.$route.path : null
        if (pendingFullPath) {
          localStorage.setItem('pendingFullPath', this.$route.path)
        }
        if (auth.checkAuth()) {
          this.getUserConfiguration()
        } else {
          this.componentName = 'Login'
          this.hasPrivacyPolicy = JSON.parse(localStorage.getItem('hasPrivacyPolicy'))
        }
      })
      .catch(() => {
        this.componentName = 'ServerError'
      })
  },
  methods: {
    ...mapMutations([
      'setFactoryTimezone',
      'setLoginOptions',
      'setLoginType',
      'setSelectedYear',
    ]),
    ...mapActions([
      'fetchConfiguration',
      'fetchConfigurationsKeys',
      'fetchModulePermissions',
      'fetchPersonNotifications',
      'fetchProfile',
    ]),
    hideModal () {
      this.hasPrivacyPolicy = false
      this.getUserConfiguration()
    },
    onLoginSuccess () {
      this.getUserConfiguration()
    },
    async getUserConfiguration () {
      if (this.hasPrivacyPolicy) {
        this.showPrivacyPolicyModal = true
        return
      }
      this.isFetchingUserConfiguration = true
      try {
        await this.fetchProfile()
      } catch (e) {
        this.isFetchingUserConfiguration = false
        if (e instanceof ProfilePasswordExpiredError) {
          this.resetPasswordUserId = e.userId
          return
        }
      }
      if ((!this.profileStore.userHasAccessPermission || !this.profileStore.roleId) && !this.profileStore.isAdministrator) {
        this.componentName = 'NotAuthorization'
        this.isFetchingUserConfiguration = false
        return
      }
      const { fetchNeilaConfiguration } = useNeilaConfigurationStore()
      let modulePermissions
      try {
        await this.fetchConfiguration()
        await this.fetchModulePermissions()
        modulePermissions = this.getAllowedModulesByUser()
      } catch (e) {
        this.handleConfigurationError(e)
        this.isFetchingUserConfiguration = false
        return
      }
      try {
        await Promise.all([
          this.fetchConfigurationsKeys(),
          this.loadDeviceConfiguration(),
          fetchNeilaConfiguration(),
        ])
        const auditSourcesStore = useAuditSourcesStore()
        await auditSourcesStore.fetch()
        this.setRoutes(modulePermissions, auditSourcesStore.auditSources)
        this.setSelectedYear(getYear())
        localStorage.setItem('hasPrivacyPolicy', this.getConfigurationKeyByName(CONFIGURATION_KEYS.ENABLE_PRIVACY_POLICY_ALERT).value)
        this.fetchPersonNotifications()  // Do not await this. Performance sink
        await this.continueNavigation()
        this.componentName = 'App'
      } catch (e) {
        this.handleConfigurationError(e)
      } finally {
        this.isFetchingUserConfiguration = false
      }
    },
    async continueNavigation () {
      const pendingFullPath = localStorage.getItem('pendingFullPath')
      const ssoRedirectPath = localStorage.getItem('SSORedirect')
      if (this.$route.path === '/' && !ssoRedirectPath) { return }
      await this.fetchApplicationData()
      if (ssoRedirectPath) {
        this.ssoRedirect()
      } else if (pendingFullPath) {
        localStorage.removeItem('pendingFullPath')
        this.secureRedirect({ path: pendingFullPath })
      }
    },
    handleConfigurationError (e) {
      if (e.status === HTTP_ERROR_BAD_REQUEST || e.toString() === `Error: ${ERROR_PERMISSION_ROLE}`) {
        this.componentName = 'NotAuthorization'
        return
      }
      throw e
    },
    setConfigurationBase () {
      this.setFactoryTimezone(this.baseConfiguration.timeZone)
      this.setLoginType(this.baseConfiguration.loginType.TYPE)
      this.setLoginOptions(this.baseConfiguration.loginType.OPTIONS || {})
    },
    getAllowedModulesByUser () {
      const modulePermissions = this.profileStore.isAdministrator ?
        this.getModulePermissions().filter(perm => perm.module_type != APP_TYPE).map(roleModule => roleModule.name) :
        this.getRoleModules.filter(perm => perm.module_type != APP_TYPE).map(roleModule => roleModule.name)
      if (modulePermissions.length ===0) {
        throw Error(ERROR_PERMISSION_ROLE)
      }
      return modulePermissions
    },
    setRoutes (modulePermissions, auditSources) {
      const routes = [
        actionRoutes,
        alertsRoutes,
        completedTasksRoutes,
        consolidatedReportInCorporativeRoutes,
        dailyRoundCheckpointsRoutes,
        dailyroundRoutes,
        diagnosisRoutes,
        gembaWalkCheckpointsRoutes,
        hseRisksRoutes,
        improvementIdeasRoutes,
        loogBookRoutes,
        lpaRoutes,
        okFirstPartRoutes,
        problemSolvingRoutes,
        rotationTableRoutes,
        shopFloorManagement,
        lineBoard,
        skillMatrixRoutes,
        standardViewerRoutes,
        standardWorkReviewRoutes,
        virtualTrainingViewerRoutes,
      ]
      auditSources.forEach(app => {
        const auditsRoutes = createNewRoutesAudits(app.name)
        routes.push(auditsRoutes)
      })

      routes.forEach((route) => {
        const currentRoute = route[0]
        // The "shownInMenu" property defines whether a route should be shown in the sidebar (Details pages, new item pages... shouldn't).
        // To allow them to be accessed, if its parent has permissions they will be added to the router
        const filteredChildren = currentRoute.children.filter(child => {
          const parentModulePermissionName = child?.meta?.parentModulePermissionName
          const hasModulePermission = modulePermissions.includes(child.name) || !child.shownInMenu
          const hasParentModulePermission = parentModulePermissionName ? modulePermissions.includes(parentModulePermissionName) : true
          const isConfigKeyForEnablingRoute = child?.configKeyForEnablingRoute ? this.getConfigurationKeyByName(child.configKeyForEnablingRoute).value : true
          return hasModulePermission && hasParentModulePermission && isConfigKeyForEnablingRoute
        })

        const moduleName = this.routeToModuleMappings[currentRoute.name] ?? currentRoute.name

        route[0].children = []
        if (this.profileStore.hasRoleModuleAccess(moduleName)) {
          route[0].children = filteredChildren
        }
      })

      const allRoutes = _flattenDeep([
        commonRoutes,
        pendingRoutes,
        myCalendar,
        this.configureRoutes(routes, auditSources),
      ])
      allRoutes.forEach((route) => {
        this.$router.addRoute(route)
      })
    },
    configureRoutes (routes, auditSources) {
      if (this.profileStore.isAdministrator || this.getRoleModulesByType(CONFIG_TYPE).length > 0) {
        const settingsRoutesToAdd = _cloneDeep(settingsRoutes[0])
        settingsRoutesToAdd.children.push(...peopleSettingsRoutes)
        settingsRoutesToAdd.children.push(...perimeterSettingsRoutes)
        settingsRoutesToAdd.children.push(...actionSettingsRoutes)
        settingsRoutesToAdd.children.push(...improvementIdeasSettingsRoutes)
        settingsRoutesToAdd.children.push(...alertSettingsRoutes)
        settingsRoutesToAdd.children.push(...routineSettingsRoutes)
        settingsRoutesToAdd.children.push(...toolSettingsRoutes)
        settingsRoutesToAdd.children.push(...visualStandardRSRSCharacteristicsSettingsRoutes)
        settingsRoutesToAdd.children.push(...sfmSettingsRoutes)
        settingsRoutesToAdd.children.push(...swaSettingsRoutes)

        let routesToAdd = createNewRoutesProductionHierarchy(this.getProductionHierarchyLevels)
        if (this.profileStore.isAdministrator) {
          routesToAdd = routesToAdd.concat(settingsSystemRoutesOptions)
          if (this.getAllowEditShifts) routesToAdd = routesToAdd.concat(settingsShiftRoutesOptions)
        }
        settingsRoutesToAdd.children.push(...routesToAdd)
        auditSources.forEach(app => {
          const source = app.name
          app.isMasterAuditSource? settingsRoutesToAdd.children.push(...masterAuditSettingsTemplateRoute(source)) : settingsRoutesToAdd.children.push(auditSettingsTemplateRoute(source))
          settingsRoutesToAdd.children.push(
            auditSettingsPreviousCommentsTimeRoute(source),
            chartsConfigRoute(source),
            auditsActionsEvidencesRoute(source),
            auditSettingsPlannerConfiguration(source),
            auditSettingsTemplateAssigmentRoute(source),
            auditSettingsSubtypesConfiguration(source),
            auditSettingsSubtypesDetails(source),
            auditSettingsObserversRoute(source)
          )
        })
        if (this.includeSkillMatrix) {
          settingsRoutesToAdd.children.push(
            auditSettingsComplianceEvaluationReasonsRoute(ILUO_SOURCE),
            auditSettingsComplianceThresholdsRoute(ILUO_SOURCE),
            auditSettingsQualityFailuresRoute(ILUO_SOURCE),
            auditSettingsNoCompliancesRoute(ILUO_SOURCE),
            auditSettingsImportSkillsRoute(ILUO_SOURCE),
            skillMatrixSettingsPeopleIdentifierFormat()
          )
        }
        routes.push([settingsRoutesToAdd])
      }
      return routes
    },
    secureRedirect (pushParams) {
      this.$router.push(pushParams).catch(error => {
        if (error.name != "NavigationDuplicated") {
          throw error
        }
      })
    },
    ssoRedirect () {
      const url = localStorage.getItem('SSORedirect')
      localStorage.removeItem('SSORedirect')
      const query = {...this.$route.query, ...{ ssoredirect: true }}
      this.secureRedirect({ 'path': url, query })
    },
  },
}

</script>
